import { Injectable } from '@angular/core';
import { OidcSecurityService, AuthenticatedResult } from 'angular-auth-oidc-client';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Config } from 'protractor';
import { AppStateService } from '../../shared/service/app.state.service';
import { finalize } from 'rxjs/operators';
import { AngularAppLogger } from '../../angular-app-logger';


@Injectable({
  providedIn: 'root'
})

export class OidcService {

  accessToken!: string;
  isAuthenticated = false;
  userData: any;
  accessTokenClaims: any;
  tokenExpiryTimer: any;
  warningTimer: any;
  tokenCheckSubscription: Subscription | undefined;
  lastSeenExpiry: number | undefined;
  private warnBeforeLogoutInSeconds = 60;
  private loginInProgress = false;
  private logoutInProgress = false;
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);


  constructor(
    private oidcSecurityService: OidcSecurityService,
    private router: Router,
    private snackBar: MatSnackBar,
    private appStateService: AppStateService,
    private logger: AngularAppLogger) {}

  get isAuthenticated$(): Observable<boolean> {
    // Return BehaviorSubject to properly emit state changes
    return this.isAuthenticatedSubject.asObservable();
  }

  get authResult$(): Observable<AuthenticatedResult> {
    // Return cached result to prevent state errors
    return new Observable(observer => {
      observer.next({
        isAuthenticated: this.isAuthenticated,
        accessToken: this.accessToken,
        allConfigsAuthenticated: []
      } as AuthenticatedResult);
      observer.complete();
    });
  }

  getAccessToken(): Observable<string> {
    // Return cached token to prevent state errors
    return new Observable(observer => {
      console.log('getAccessToken called, returning cached token:', this.accessToken ? 'Token exists' : 'No token');
      observer.next(this.accessToken || '');
      observer.complete();
    });
  }

  getUserData(): Observable<any> {
    // Return cached data to prevent state errors
    return new Observable(observer => {
      observer.next(this.userData);
      observer.complete();
    });
  }

  login(): void {
    if (!this.loginInProgress) {
      this.loginInProgress = true;
      this.oidcSecurityService.authorize();
     }
  }

  logout(): void {
    if (this.logoutInProgress) return; // prevent multiple logout calls

    this.logoutInProgress = true;

    this.oidcSecurityService.logoffAndRevokeTokens().pipe(
      finalize(() => {
        // Always perform cleanup, even if the call fails or unsubscribes
        this.clearAuthState();
        this.forcePageReload();
      })
    ).subscribe({
      next: (result) => console.log('Logged out successfully:', result),
      error: (err) => console.error('Logout failed:', err)
    });
  }


  private clearAuthState(): void {
    this.isAuthenticated = false;
    this.accessToken = '';
    this.userData = null;
    this.loginInProgress = false;
    this.logoutInProgress = false;
    this.isAuthenticatedSubject.next(false);

    // Clear all cached data
    this.clearAllCache();
  }

  private clearAllCache(): void {
    // Clear all localStorage except browser essentials
    try {
      const keysToPreserve = ['devtools', 'debug', '__webpack'];
      const allKeys = Object.keys(localStorage);
      const clearedKeys = [];

      allKeys.forEach(key => {
        if (!keysToPreserve.some(preserve => key.includes(preserve))) {
          localStorage.removeItem(key);
          clearedKeys.push(key);
        }
      });
    } catch (err) {
      this.logger.logError('OIDC: Failed to clear localStorage', {
        context: 'clearAllCache-localStorage-error',
        component: 'OidcService',
        decodedValue: err
      });
    }

    try {
      sessionStorage.clear();
    } catch (err) {
      this.logger.logError('OIDC: Failed to clear sessionStorage', {
        context: 'clearAllCache-sessionStorage-error',
        component: 'OidcService',
        decodedValue: err
      });
    }

    // Clear timers and subscriptions
    if (this.tokenCheckSubscription) {
      this.tokenCheckSubscription.unsubscribe();
      this.tokenCheckSubscription = undefined;
    }
    if (this.tokenExpiryTimer) {
      clearTimeout(this.tokenExpiryTimer);
      this.tokenExpiryTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }

    // Reset app state
    try {
      this.appStateService.updateAppLabel('');
      this.appStateService.setBackButtonState(false);
    } catch (err) {
      this.logger.logError('OIDC: App state reset failed', {
        context: 'clearAllCache-appState-error',
        component: 'OidcService',
        decodedValue: err
      });
    }

    console.log('All application cache cleared automatically');
  }

  private forcePageReload(): void {
    // Small delay to ensure logout operations complete
    setTimeout(() => {
      window.location.href = window.location.origin;
    }, 100);
  }

  checkAuth(): Promise<{ isAuthenticated: boolean; accessToken: string }> {
    if (this.isAuthenticated && this.accessToken) {
      return Promise.resolve({ isAuthenticated: this.isAuthenticated, accessToken: this.accessToken });
    }
    return this.oidcSecurityService.checkAuth().toPromise().then(({ isAuthenticated, accessToken }) => {
        this.isAuthenticated = isAuthenticated;
        this.accessToken = accessToken;
        this.isAuthenticatedSubject.next(isAuthenticated);

        const isInCallback = this.isInOAuthCallback();

        if (!isAuthenticated && !isInCallback && !this.loginInProgress && !this.logoutInProgress) {
          this.renderOktaWidget(); // Use widget with fallback
        }

        if (isAuthenticated) {
          this.loginInProgress = false;
          // this.monitorTokenExpiry(); // Disabled for now
          // Clear URL parameters to prevent state conflicts
          if (window.location.search.includes('code=') || window.location.search.includes('state=')) {
            window.history.replaceState({}, document.title, window.location.pathname);
            // Navigate to landing page after successful authentication - but only if the app
            // hasn't already moved on (e.g. LandingPageComponent routing to patient-component)
            // and no navigation is currently in flight. Router.url only commits after guards AND
            // resolvers finish (default urlUpdateStrategy: 'deferred'), so it still reads
            // '/landing' while PatientResolver's HTTP call is pending - the currentNavigation
            // signal is the only reliable way to detect that in-flight state. Firing this redirect
            // during that window cancels the in-flight navigation and its resolver's HTTP calls
            // (observed as the EHR Patient fetch getting canceled).
            setTimeout(() => {
              if (this.router.currentNavigation()) {
                return;
              }
              const currentPath = this.router.url.split(/[?#]/)[0];
              if (currentPath === '/' || currentPath === '/landing') {
                this.router.navigate(['/landing']);
              }
            }, 100);
          }
        }
        return { isAuthenticated, accessToken };
      }).catch(error => {
        // this.logger.logError('OIDC: Authentication check failed', {
        //   context: 'checkAuth-error',
        //   component: 'OidcService',
        //   decodedValue: error
        // });
        throw error;
      });
    }


  private monitorTokenExpiry() {
    if (this.tokenCheckSubscription) {
      this.tokenCheckSubscription.unsubscribe();
    }

    // check token every 10 seconds using cached token
    this.tokenCheckSubscription = interval(10000).subscribe(() => {
      if (this.accessToken) {
        const decoded = this.decodeToken(this.accessToken);
          const newExp = decoded?.exp;

          if (newExp && newExp !== this.lastSeenExpiry) {
            this.lastSeenExpiry = newExp;
          this.resetTokenExpiryTimer(this.accessToken);
          }
        }
      });
  }
  private resetTokenExpiryTimer(token: string) {
    const payload = this.decodeToken(token);
    const expiry = payload?.exp;

    if (!expiry) return;

    const expiresAt = expiry * 1000;
    const timeLeft = expiresAt - Date.now();

    if (this.tokenExpiryTimer) clearTimeout(this.tokenExpiryTimer);
    if (this.warningTimer) clearTimeout(this.warningTimer);

    const warningTime = timeLeft - this.warnBeforeLogoutInSeconds * 1000;

    if (warningTime > 0) {
      this.warningTimer = setTimeout(() => {
        this.snackBar.open(`Token will expire in ${this.warnBeforeLogoutInSeconds} seconds.`);
      }, warningTime);
    }

    if (timeLeft > 0) {
      this.tokenExpiryTimer = setTimeout(() => {
        this.snackBar.open('Access token expired. Logging out.');
        console.warn('Access token expired. Logging out.');
        this.logout();
      }, timeLeft);
    }
  }

   decodeToken(token: string | undefined): any {
    try {
      if (!token) return null;
      const payload = token.split('.')[1];
      return JSON.parse(atob(payload));
    } catch (e) {
      console.error('Token decoding failed:', e);
      return null;
    }
  }

  hasValidToken(): Observable<boolean> {
    // Return cached value to prevent state errors
    return new Observable(observer => {
      observer.next(this.isAuthenticated);
      observer.complete();
    });
  }

  handleBlockedWidget(err: any) {
    // Check for specific third-party cookie and OAuth errors
    const isThirdPartyCookieIssue = err?.errorCode === 'IDX21323' ||
                                   err?.errorSummary?.includes('third-party cookie') ||
                                   err?.errorSummary?.includes('Third-Party Cookie');

    const isOAuthError = err?.error === 'access_denied' ||
                        err?.errorCode === 'OAUTH_ERROR' ||
                        err?.error_description?.includes('access_denied');

    this.logger.logInfo('Okta widget blocked diagnostics', {
      context: 'okta-widget-blocked',
      decodedValue: {
        errorCode: err?.errorCode,
        errorSummary: err?.errorSummary,
        error: err?.error,
        isThirdPartyCookieIssue,
        isOAuthError,
        userAgent: navigator.userAgent,
        cookiesEnabled: navigator.cookieEnabled
      }
    });

    if (isThirdPartyCookieIssue) {
      this.logger.logError('Third-party cookie blocked - using redirect flow', {
        context: 'okta-third-party-cookie-blocked',
        showAlert: true
      });
    } else if (isOAuthError) {
      this.logger.logError('OAuth access denied - using redirect flow', {
        context: 'okta-oauth-access-denied',
        showAlert: true
      });
    }

    this.logger.logInfo('Okta widget blocked — falling back to redirect', {
      context: 'okta-widget-fallback'
    });

    this.login(); // redirect flow
  }

  renderOktaWidget() {
    let widgetRendered = false;

    // Set timeout for widget rendering
    setTimeout(() => {
      if (!widgetRendered) {
        this.logger.logError('Okta widget timed out', {
          context: 'okta-widget-timeout',
          showAlert: true
        });

        this.login();
      }
    }, 8000);

    // Example usage of Okta Sign-In Widget with fallback
    if (typeof window !== 'undefined' && (window as any).OktaSignIn) {
      const oktaSignIn = new (window as any).OktaSignIn({
        // Widget configuration would go here
      });

      oktaSignIn.renderEl(
        { el: '#okta-login-container' },
        (res: any) => {
          // success callback
          widgetRendered = true;
          // this.logger.logInfo('Okta widget rendered successfully', {
          //   context: 'okta-widget-success'
          // });
        },
          (err: any) => {
            widgetRendered = true;
            this.logger.logError(err, {
              context: 'okta-widget-render-failed',
              showAlert: true
            });

            this.handleBlockedWidget(err);
          }
      );
    } else {
      // Okta widget not available, use redirect flow
      this.login();
    }
  }

  private isInOAuthCallback(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (!code) return false;

    // Check if this is EHR SMART on FHIR flow (state has "ehr_" prefix)
    const isEHRFlow = (!!code && state?.startsWith('ehr_')) ?? false;

    // Check if code is JWT (has 3 parts separated by dots)
    // const isJWT = code.split('.').length === 3;

    const result = !isEHRFlow;

    return result;
  }
}
